import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

import { sections } from '../../../../common/config';
import useScrollStore from '../../../../common/store/useScrollStore';
import useMediaStore from '../../../../common/store/useMediaStore';

const getThreshold = () => sections.height.landing / 100 * window.innerHeight;

const useFloatAnimation = (refs: React.RefObject<THREE.Group>[]) => {
  const play = useRef(true);
  const threshold = useRef(getThreshold());
  const tweens = useRef<GSAPTween[]>([]);

  function animate() {
    tweens.current.forEach(tween => tween.play());
    play.current = true;
  }

  function stop() {
    tweens.current.forEach(tween => tween.pause());
    play.current = false;
  }

  /**
   * Setup animation
   */
  useEffect(() => {
    refs.forEach(ref => {
      const tween = gsap.to(ref.current!.position, {
        duration: (Math.random() * 0.5) + 1.75,
        y: 0.1,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
        repeatDelay: 0.2
      });

      tweens.current.push(tween);
    });

    return () => {
      tweens.current.forEach(tween => tween.kill());
      tweens.current.length = 0;
    }
  }, []);

  /**
   * onResize
   */
  useEffect(() => {
    function handleResize() {
      threshold.current = getThreshold();

      const md = useMediaStore.getState().md;
      md ? animate() : stop();
    }

    window.addEventListener("resize", handleResize);
    return () => { window.removeEventListener("resize", handleResize) }
  }, [])

  /**
   * Set threshold for animation, stop when > 100vh, play when <= 100vh
   */
  useEffect(() => useScrollStore.subscribe(state => {
    if (
      (-state.position > threshold.current || !useMediaStore.getState().md)
      && play.current
    ) {
      stop();
    }
    else if (
      -state.position <= threshold.current
      && useMediaStore.getState().md
      && !play.current
    ) {
      animate();
    }
  }), []);
}

export default useFloatAnimation;